---
title: "Writing good prompts for Choosie"
description: "How to prompt Choosie well: specific beats vague, give it the criteria, multi-step requests are fine. Good versus weak prompt examples for the AI assistant."
category: "hq"
keywords:
  - "Choosie prompts"
  - "how to prompt AI assistant"
  - "good prompts ATS"
  - "Choosie prompt examples"
  - "novel chart prompts"
  - "cross-tab chart prompts"
schema: "HowTo"
nav:
  section: "choosie"
  order: 40
  parent: "/choosie"
---

[Choosie](/choosie) is only as good as what you ask it. It reads your message and decides what to search for or what plan to propose. A specific request gets a specific result; a vague one makes Choosie guess. This page is how to phrase a prompt so it gets it right the first time.

## Specific beats vague

The single biggest lever. A vague prompt forces Choosie to guess your criteria, and it guesses generically.

| Weak | Better |
| --- | --- |
| "Find good candidates." | "Find candidates with 5+ years of Python who applied to the Backend Engineer role." |
| "Show me the pipeline." | "How many candidates are in the interview stage across all open jobs?" |
| "Update this candidate." | "Set Priya Nair's source to 'Referral' and her position to 'Staff Engineer'." |
| "Schedule the interview." | "Create a scheduling request for the Backend Engineer first-round, for the three candidates in the screening stage." |

"Good" and "the interview" mean something to you and nothing to Choosie. The fix is always the same: name the criteria, the role, the stage, the people.

## Give it the criteria, not the conclusion

When you want a search, hand Choosie the filters — not the verdict you've already reached. "Strong candidates" is a conclusion. "Candidates with a match rating above 80% who've had at least one Hire on their feedback" is criteria Choosie can act on. Tell it *what makes* a candidate a match for this search, and it can find them.

## Multi-step requests are fine

You don't have to break a task into one instruction per message. Choosie handles a request with several steps and, for changes, rolls them into a single [plan](/choosie/plans-and-approvals) you approve once.

A reasonable multi-step ask:

> "Find candidates for the Frontend role with React and TypeScript, add the top three to that job, and move them to the screening stage."

Choosie runs the search itself, then proposes a plan: add three named candidates, move three stages. You see all six steps before anything happens. One prompt, one review.

## Name records when you can

If you already know the candidate or job, say so by name. "Move Daniel Osei to offer on the Staff Designer role" leaves no ambiguity. "Move that candidate to the next stage" makes Choosie work out who and which stage from the conversation — usually right, but "name it explicitly" is the safer habit. Choosie never invents records, so a clear name is the cleanest path.

<Callout type="tip" title="If a plan looks wrong, the prompt was probably loose">
A plan that targets the wrong record or has an extra step is almost always a too-broad prompt. [Reject the plan](/choosie/plans-and-approvals) — rejecting changes nothing — and ask again with the specific name, role, or stage. Re-prompting is faster than approving and fixing.
</Callout>

## Asking for a novel chart

Choosie can compose charts and tables that no built-in tool covers — see [charts and tables in Choosie](/choosie/charts-and-tables#novel-charts-on-demand) for the long version. Two prompt patterns that work well:

- **"Avg X by Y"** triggers a 1-D composed chart. Examples: *"avg match score by skill"*, *"avg years of experience by source"*, *"avg feedback by interviewer"*.
- **"X by Y broken down by Z"** triggers a cross-tab (stacked bar by default). Examples: *"candidates by skill broken down by stage"*, *"applications by source broken down by department"*. Adding *"as a heatmap"* or *"as a stacked bar"* forces the chart kind.

If you don't know what dimensions a source supports, ask "what cross-tabs can you do across applications?" and Choosie will list the legal dims and metrics for that source before composing.

## When to skip Choosie and use the UI

Choosie is not always the fast path, and a good prompt includes knowing when not to write one.

- **A single drag is quicker than a sentence.** Moving one candidate between two columns on the [board](/guides/candidates/table-and-boards) beats describing it.
- **Bulk visual work belongs in the table.** Multi-selecting rows and editing in the [candidates table](/guides/candidates) is faster than narrating each change.
- **Precise form work belongs in the form.** Building a job's [application questions](/guides/careers/application-questions) is easier in the editor, where you see the whole thing.

Choosie earns its keep on searches, questions that span many records, and multi-step changes. For one quick action, the click usually wins.

## When it gets it wrong

If Choosie misreads a request, it's nearly always the prompt — too vague, or a pronoun where a name belonged. Rephrase with concrete detail and try again. A long, wandering chat also makes Choosie less sharp, because it has more to reconcile each turn; for a fresh task, start a fresh chat. And Choosie's searches obey the same rules as the rest of Chosen — if one comes back empty, [why a search returns no results](/guides/search/why-no-results) is the place to look.
